/**
 * CustomAccessoryForm — S7 inline form (body-swap inside AccessoryPicker slideover).
 *
 * Plan reference: PR-02b §1b.3
 * Wire: QB-LINE-line-item-accessories-FLOW.html (S7)
 *
 * Requirements:
 * - Fields: name (required), unit (required, controlled enum), unit cost (required).
 * - Destination sentence rendered VERBATIM from wire, BEFORE the commit (R3):
 *     "Saves to Settings › Item Catalog for {area name} and will be selectable
 *      on future quotes. It is added to this line at the same time." (IDI-724)
 * - Does NOT open a second overlay (U-18) — body swap only.
 * - Save calls onSave(draft) then the parent handles the catalog write +
 *   line-item add. This form only validates and emits.
 */

import React, { useState } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { SelectMenu } from '../../ui';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

// Mirror ACCESSORY_UNITS in QuoteItemsPage.js (single source of truth would be nicer;
// deferred to a follow-up so PR-02b doesn't leak into settings).
const UNITS = [
  { value: 'box', label: 'box' },
  { value: 'roll', label: 'roll' },
  { value: 'can', label: 'can' },
  { value: 'bag', label: 'bag' },
  { value: 'each', label: 'each' },
];

export default function CustomAccessoryForm({ areaLabel, defaultName = '', onCancel, onSave }) {
  const [name, setName] = useState(defaultName);
  const [unit, setUnit] = useState('');
  const [costDollars, setCostDollars] = useState(''); // display as dollars; convert to cents on save
  const [saving, setSaving] = useState(false);

  const canSave = Boolean(name.trim() && unit && costDollars !== '' && Number(costDollars) >= 0);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave?.({
        name: name.trim(),
        unit,
        unit_cost: Math.round(Number(costDollars) * 100), // to cents
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>
        Add custom accessory
      </div>

      <Input
        label="Name *"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Foam adhesive"
        autoFocus
      />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-default)' }}>Unit *</label>
          <SelectMenu
            width="full"
            value={unit}
            onChange={(val) => setUnit(val)}
            placeholder="Select unit…"
            options={UNITS}
          />
        </div>
        <Input
          label="Cost per unit *"
          type="number"
          value={costDollars}
          onChange={(e) => setCostDollars(e.target.value)}
          placeholder="0.00"
          step="0.01"
          min="0"
        />
      </div>

      {/* R3 destination sentence — rendered BEFORE the commit */}
      <div
        className="flex items-start gap-2 text-xs p-2.5 rounded"
        style={{
          backgroundColor: 'var(--color-status-neutral-bg)',
          color: 'var(--color-text-default)',
          border: '1px solid var(--color-border)',
        }}
      >
        <InformationCircleIcon className="h-4 w-4 flex-shrink-0 mt-px" style={{ color: 'var(--color-text-muted)' }} />
        <span>
          {/* IDI-724: the accessory is an Item Catalog row scoped to this area. */}
          Saves to <b>Settings › Item Catalog</b> for {areaLabel || 'this area'} and will be selectable on future quotes.
          It is added to this line at the same time.
        </span>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onCancel} disabled={saving}>Back</Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave || saving}>
          {saving ? 'Saving…' : 'Save & add'}
        </Button>
      </div>
    </div>
  );
}
